export type Getter<Source, Return = any> = (src: Source, index?: number) => Return;

export interface DocxDescriptor<Source, Item = any> {
  codeField: string;
  codeValue: Getter<Source>;
  date: Getter<Source, Date | string>;
  header: Record<string, Getter<Source>>;
  materials?: {
    list: Getter<Source, Item[]>;
    item: Record<string, Getter<Item>>;
  };
  // Generic list mapping to support arbitrary list keys such as "products"
  items?: {
    key: string; // e.g., "materials" | "products"
    list: Getter<Source, Item[]>;
    item: Record<string, Getter<Item>>;
  };
}

export function buildDocxData<Source, Item = any>(
  src: Source,
  descriptor: DocxDescriptor<Source, Item>,
  overrides?: Record<string, any>
) {
  const rawDate = descriptor.date(src);
  const date = rawDate instanceof Date ? rawDate : new Date(rawDate as unknown as string);

  const header = Object.fromEntries(
    Object.entries(descriptor.header).map(([key, getter]) => [key, (getter as Getter<Source>)(src)])
  );

  const base: Record<string, any> = {
    ...header,
    [descriptor.codeField]: descriptor.codeValue(src),
    day: date.getDate(),
    month: date.getMonth() + 1,
    year: date.getFullYear(),
  };

  if (descriptor.materials) {
    const items = descriptor.materials.list(src) || [];
    base.materials = items.map((item, index) => {
      const mapped = Object.fromEntries(
        Object.entries(descriptor.materials!.item).map(([key, getter]) => [
          key,
          (getter as Getter<Item>)(item, index),
        ])
      );
      return { index: index + 1, ...mapped };
    });
  } else if (descriptor.items) {
    const items = descriptor.items.list(src) || [];
    base[descriptor.items.key] = items.map((item, index) => {
      const mapped = Object.fromEntries(
        Object.entries(descriptor.items!.item).map(([key, getter]) => [
          key,
          (getter as Getter<Item>)(item, index),
        ])
      );
      return { index: index + 1, ...mapped };
    });
  }

  return { ...base, ...(overrides || {}) };
}


